// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown, Download, Printer } from "lucide-react";
import {
  SheetColumn,
  SheetScope,
  buildCsv,
  downloadCsv,
} from "@/lib/liveSheets";
import EditableCell from "./EditableCell";
import NotesCell from "./NotesCell";
import PresenceBar from "./PresenceBar";
import { parseLocalDate } from '@/utils/formatters';

interface Props {
  scope: SheetScope;
  token: string;
  data: any;
  columns: SheetColumn[];
  idField: string;
  permission: "view" | "comment" | "edit";
  presence: any[];
  refetch: () => void;
  showNotes?: boolean;
  showSummary?: boolean;
  embedded?: boolean;
}

const fmtCurrency = (n: any) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

export default function SheetView({
  scope,
  token,
  data,
  columns,
  idField,
  permission,
  presence,
  refetch,
  showNotes = false,
  showSummary = false,
  embedded = false,
}: Props) {
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [divFilter, setDivFilter] = useState("all");
  const [payFilter, setPayFilter] = useState("all");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    setRows(data?.registrations ?? []);
  }, [data]);

  const event = data?.event;
  const tokenId = data?.token_id;

  const onUpdate = (rowId: string, key: string, value: any) => {
    setRows((prev) => prev.map((r) => (r[idField] === rowId ? { ...r, [key]: value } : r)));
  };

  const divisions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.division).filter(Boolean))).sort(),
    [rows]
  );
  const payStatuses = useMemo(
    () => Array.from(new Set(rows.map((r) => r.payment_status).filter(Boolean))).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    let out = rows;
    const q = search.toLowerCase().trim();
    if (q) {
      out = out.filter((r) =>
        Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q))
      );
    }
    if (divFilter !== "all") out = out.filter((r) => r.division === divFilter);
    if (payFilter !== "all") out = out.filter((r) => r.payment_status === payFilter);
    if (sortKey) {
      out = [...out].sort((a, b) => {
        const av = a[sortKey] ?? "";
        const bv = b[sortKey] ?? "";
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [rows, search, divFilter, payFilter, sortKey, sortDir]);

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const summary = useMemo(() => {
    const paid = rows.filter((r) => r.payment_status === "paid").length;
    const pending = rows.filter((r) => r.payment_status !== "paid").length;
    const collected = rows.reduce((s, r) => s + Number(r.paid || 0), 0);
    const owed = rows.reduce((s, r) => s + Number(r.owed || 0), 0);
    return { teams: rows.length, paid, pending, collected, owed };
  }, [rows]);

  const exportCsv = () => {
    downloadCsv(`${event?.name || "sheet"}-${scope}.csv`, buildCsv(columns, filtered));
  };

  const venueLine = [event?.venue_name, event?.venue_city, event?.venue_state]
    .filter(Boolean)
    .join(", ");
  const dateLine =
    event?.start_date
      ? `${format(parseLocalDate(event.start_date), "MMM d, yyyy")}${
          event.end_date ? " – " + format(parseLocalDate(event.end_date), "MMM d, yyyy") : ""
        }`
      : "";

  return (
    <div className={embedded ? "flex flex-col bg-white" : "flex h-screen flex-col overflow-hidden bg-white"}>
      {/* Header */}
      {!embedded && (
        <header className="flex items-center justify-between gap-4 bg-[#002E62] px-4 py-3 text-white print:bg-white print:text-black">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-bold">{event?.name}</h1>
            <p className="truncate text-xs text-white/80 print:text-black">
              {[venueLine, dateLine].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            <PresenceBar users={presence} />
            <Button size="sm" variant="secondary" onClick={exportCsv}>
              <Download className="mr-1 h-4 w-4" /> Export CSV
            </Button>
            <Button size="sm" variant="secondary" onClick={() => window.print()}>
              <Printer className="mr-1 h-4 w-4" /> Print
            </Button>
          </div>
        </header>
      )}

      {/* Summary */}
      {showSummary && (
        <div className="flex flex-wrap gap-6 border-b bg-white px-4 py-2 text-sm">
          <span><b>{summary.teams}</b> Teams</span>
          <span className="text-green-700"><b>{summary.paid}</b> Paid</span>
          <span className="text-amber-700"><b>{summary.pending}</b> Pending</span>
          <span><b>{fmtCurrency(summary.collected)}</b> Collected</span>
          <span className="text-red-700"><b>{fmtCurrency(summary.owed)}</b> Owed</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 border-b bg-slate-50 px-4 py-2 print:hidden">
        <Input
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-56 bg-white"
        />
        {divisions.length > 0 && (
          <Select value={divFilter} onValueChange={setDivFilter}>
            <SelectTrigger className="h-8 w-40 bg-white"><SelectValue placeholder="Division" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All divisions</SelectItem>
              {divisions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {payStatuses.length > 0 && (
          <Select value={payFilter} onValueChange={setPayFilter}>
            <SelectTrigger className="h-8 w-40 bg-white"><SelectValue placeholder="Payment" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payment</SelectItem>
              {payStatuses.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {filtered.length} of {rows.length}
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-max border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-[#002E62] text-white print:bg-slate-200 print:text-black">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  className={`cursor-pointer whitespace-nowrap px-3 py-2 text-left font-medium ${c.width || ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    <ArrowUpDown className="h-3 w-3 opacity-60" />
                  </span>
                </th>
              ))}
              {showNotes && <th className="px-3 py-2 text-left font-medium">Notes</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (showNotes ? 1 : 0)} className="py-8 text-center text-muted-foreground">
                  No rows.
                </td>
              </tr>
            ) : (
              filtered.map((row, idx) => (
                <tr key={row[idField]} className={idx % 2 ? "bg-slate-50" : "bg-white"}>
                  {columns.map((c) => (
                    <td key={c.key} className={`whitespace-nowrap px-3 py-1.5 align-middle ${c.width || ""}`}>
                      <EditableCell
                        row={row}
                        col={c}
                        scope={scope}
                        token={token}
                        permission={permission}
                        onUpdate={onUpdate}
                        idField={idField}
                      />
                    </td>
                  ))}
                  {showNotes && (
                    <td className="px-3 py-1.5 align-middle">
                      <NotesCell
                        registrationId={row[idField]}
                        eventId={event?.id}
                        tokenId={tokenId}
                        notes={row.notes || []}
                        permission={permission}
                        onAdded={refetch}
                      />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
